import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { inventoryAPI, InventoryItem, InventoryUsage } from '../api/inventory';
import toast from 'react-hot-toast';
import apiClient from '../api/client';
import { usePermissions } from '../utils/usePermissions';
import { formatUnit } from '../utils/helpers';
import QRCode from 'qrcode';
import { Html5Qrcode } from 'html5-qrcode';

// Premium Stock Level Indicator
function StockBar({ qty, min, unit }: { qty: number; min: number; unit?: string }) {
  const numQty = Number(qty) || 0;
  const numMin = Number(min) || 0;
  const pct = numMin > 0 ? Math.min((numQty / (numMin * 3)) * 100, 100) : (numQty > 0 ? 100 : 0);
  const isLow = numQty <= numMin;
  const barColor = isLow 
    ? 'bg-gradient-to-r from-performance-red to-[#930000] shadow-[0_0_10px_rgba(255,43,43,0.5)]' 
    : 'bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.4)]';
  const txtColor = isLow 
    ? 'text-performance-red font-bold animate-pulse' 
    : 'text-emerald-400';
  const unitLabel = formatUnit(unit);

  return (
    <div className="flex items-center gap-3 w-full max-w-[200px]">
      <div className="flex-1 h-2 bg-black/40 rounded-full border border-white/5 overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`font-data-sm text-xs tabular-nums ${txtColor}`}>{numQty} {unitLabel}</span>
    </div>
  );
}

// ─── QR SCANNER CAMERA MODAL ─────────────────────────
function QRScannerModal({
  onScanSuccess,
  onClose,
  targetMode,
}: {
  onScanSuccess: (code: string) => void;
  onClose: () => void;
  targetMode: 'add' | 'deduct';
}) {
  const [errorMsg, setErrorMsg] = useState<string>('');

  useEffect(() => {
    let html5QrcodeScanner: Html5Qrcode | null = null;
    let isStopped = false;

    const startScanner = async () => {
      try {
        html5QrcodeScanner = new Html5Qrcode("qr-reader");
        await html5QrcodeScanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          (decodedText) => {
            if (!isStopped) {
              isStopped = true;
              html5QrcodeScanner?.stop().then(() => {
                onScanSuccess(decodedText);
              }).catch(() => {
                onScanSuccess(decodedText);
              });
            }
          },
          () => {}
        );
      } catch (err: any) {
        console.error("Camera error:", err);
        setErrorMsg("Could not access camera. Please allow camera permissions or ensure camera is available.");
      }
    };

    startScanner();

    return () => {
      if (html5QrcodeScanner && html5QrcodeScanner.isScanning) {
        html5QrcodeScanner.stop().catch(() => {});
      }
    };
  }, []);

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-md flex items-center justify-center z-[80] p-4 animate-fade-in">
      <div className="bg-[#0e0e0e] border border-white/10 rounded-3xl w-full max-w-md p-6 relative overflow-hidden shadow-2xl space-y-4">
        <div className="absolute top-0 left-0 w-full h-[3px] bg-performance-red"></div>
        <div className="flex justify-between items-center">
          <div>
            <h3 className="font-display-hero text-lg font-black text-white tracking-tight uppercase flex items-center gap-2">
              <span className="material-symbols-outlined text-performance-red">qr_code_scanner</span>
              Scan QR ({targetMode === 'add' ? 'Add Stock' : 'Deduct Stock'})
            </h3>
            <p className="text-[10px] text-tertiary/60 uppercase tracking-wider mt-0.5">
              Point camera at product QR code
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-tertiary hover:text-white p-1 hover:bg-white/5 rounded-lg">
            <span className="material-symbols-outlined text-[20px]">close</span>
          </button>
        </div>

        {errorMsg ? (
          <div className="p-4 bg-performance-red/10 border border-performance-red/20 rounded-xl text-center text-xs text-performance-red">
            {errorMsg}
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-white/10 bg-black p-2">
            <div id="qr-reader" className="w-full rounded-xl overflow-hidden" />
          </div>
        )}

        <button
          type="button"
          onClick={onClose}
          className="w-full py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-xl text-xs font-bold uppercase transition-all"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── SINGLE ITEM QR CODE MODAL ─────────────────────────
function ItemQRModal({ item, onClose }: { item: InventoryItem; onClose: () => void }) {
  const [qrUrl, setQrUrl] = useState<string>('');

  useEffect(() => {
    const code = item.qr_code || item.item_code;
    QRCode.toDataURL(code, { width: 300, margin: 2, color: { dark: '#000000', light: '#ffffff' } })
      .then(url => setQrUrl(url))
      .catch(err => console.error(err));
  }, [item]);

  const handleDownload = () => {
    if (!qrUrl) return;
    const a = document.createElement('a');
    a.href = qrUrl;
    a.download = `QR_${item.item_code}_${item.name.replace(/\s+/g, '_')}.png`;
    a.click();
  };

  const handlePrint = () => {
    const printWin = window.open('', '_blank');
    if (!printWin) return;
    printWin.document.write(`
      <html>
        <head>
          <title>QR Code - ${item.name}</title>
          <style>
            body { font-family: sans-serif; display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; margin: 0; }
            .card { border: 2px solid #000; padding: 20px; text-align: center; border-radius: 12px; max-width: 300px; }
            img { width: 200px; height: 200px; }
            h2 { margin: 10px 0 5px 0; font-size: 18px; }
            p { margin: 2px 0; font-size: 12px; color: #555; }
            .code { font-family: monospace; font-weight: bold; font-size: 14px; background: #eee; padding: 3px 8px; border-radius: 4px; display: inline-block; margin-top: 5px; }
          </style>
        </head>
        <body>
          <div class="card">
            <h2>${item.name}</h2>
            <p>${item.brand || 'PPF Brand'}</p>
            <img src="${qrUrl}" />
            <div><span class="code">${item.qr_code || item.item_code}</span></div>
          </div>
          <script>window.onload = () => { window.print(); window.close(); }</script>
        </body>
      </html>
    `);
    printWin.document.close();
  };

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-md flex items-center justify-center z-[80] p-4 animate-fade-in">
      <div className="bg-[#0e0e0e] border border-white/10 rounded-3xl w-full max-w-sm p-6 relative overflow-hidden shadow-2xl space-y-4 text-center">
        <div className="absolute top-0 left-0 w-full h-[3px] bg-performance-red"></div>
        <div className="flex justify-between items-center text-left">
          <div>
            <h3 className="font-display-hero text-base font-black text-white uppercase">{item.name}</h3>
            <p className="text-[10px] text-tertiary/60 uppercase tracking-wider">{item.item_code}</p>
          </div>
          <button type="button" onClick={onClose} className="text-tertiary hover:text-white p-1 hover:bg-white/5 rounded-lg">
            <span className="material-symbols-outlined text-[20px]">close</span>
          </button>
        </div>

        {qrUrl ? (
          <div className="bg-white p-4 rounded-2xl inline-block shadow-lg mx-auto">
            <img src={qrUrl} alt={item.name} className="w-48 h-48 mx-auto" />
          </div>
        ) : (
          <div className="h-48 flex items-center justify-center text-tertiary text-xs">Generating QR...</div>
        )}

        <div className="text-xs text-tertiary font-mono">
          Code: <span className="text-white font-bold">{item.qr_code || item.item_code}</span>
        </div>

        <div className="grid grid-cols-2 gap-2 pt-2">
          <button
            type="button"
            onClick={handleDownload}
            className="py-2.5 px-3 bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-xl text-xs font-bold uppercase transition-all flex items-center justify-center gap-1.5"
          >
            <span className="material-symbols-outlined text-[16px]">download</span>
            Download PNG
          </button>
          <button
            type="button"
            onClick={handlePrint}
            className="py-2.5 px-3 bg-gradient-to-r from-performance-red to-[#93000a] text-white rounded-xl text-xs font-bold uppercase transition-all flex items-center justify-center gap-1.5 shadow-md"
          >
            <span className="material-symbols-outlined text-[16px]">print</span>
            Print QR
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── BULK PRINT ALL QR CODES MODAL ─────────────────────────
function PrintAllQRModal({ items, onClose }: { items: InventoryItem[]; onClose: () => void }) {
  const [qrMap, setQrMap] = useState<Record<number, string>>({});

  useEffect(() => {
    const generateAll = async () => {
      const map: Record<number, string> = {};
      for (const item of items) {
        const code = item.qr_code || item.item_code;
        try {
          map[item.id] = await QRCode.toDataURL(code, { width: 200, margin: 2 });
        } catch (e) {
          console.error(e);
        }
      }
      setQrMap(map);
    };
    generateAll();
  }, [items]);

  const handlePrintAll = () => {
    const printWin = window.open('', '_blank');
    if (!printWin) return;

    const cardsHtml = items.map(item => `
      <div class="card">
        <h3>${item.name}</h3>
        <p>${item.brand || 'PPF'}</p>
        <img src="${qrMap[item.id] || ''}" />
        <div class="code">${item.qr_code || item.item_code}</div>
      </div>
    `).join('');

    printWin.document.write(`
      <html>
        <head>
          <title>Inventory QR Codes Sheet</title>
          <style>
            body { font-family: sans-serif; padding: 20px; background: #fff; color: #000; }
            h1 { text-align: center; font-size: 20px; margin-bottom: 20px; text-transform: uppercase; }
            .grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 15px; }
            .card { border: 1.5px dashed #444; border-radius: 8px; padding: 12px; text-align: center; page-break-inside: avoid; }
            .card h3 { margin: 0 0 4px 0; font-size: 13px; font-weight: bold; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
            .card p { margin: 0 0 6px 0; font-size: 10px; color: #666; }
            .card img { width: 130px; height: 130px; }
            .card .code { font-family: monospace; font-size: 11px; font-weight: bold; background: #f0f0f0; padding: 2px 6px; border-radius: 4px; display: inline-block; margin-top: 4px; }
            @media print {
              body { padding: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <h1>Inventory QR Code Sheet (${items.length} Products)</h1>
          <div class="grid">${cardsHtml}</div>
          <script>window.onload = () => { window.print(); window.close(); }</script>
        </body>
      </html>
    `);
    printWin.document.close();
  };

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-md flex items-center justify-center z-[80] p-4 animate-fade-in">
      <div className="bg-[#0e0e0e] border border-white/10 rounded-3xl w-full max-w-4xl p-6 relative overflow-hidden shadow-2xl space-y-4 max-h-[90vh] flex flex-col">
        <div className="absolute top-0 left-0 w-full h-[3px] bg-performance-red"></div>
        <div className="flex justify-between items-center">
          <div>
            <h3 className="font-display-hero text-lg font-black text-white uppercase flex items-center gap-2">
              <span className="material-symbols-outlined text-performance-red">qr_code_2</span>
              Print Sheet: All Inventory QR Codes ({items.length})
            </h3>
            <p className="text-[10px] text-tertiary/60 uppercase tracking-wider mt-0.5">
              Printable stickers sheet for product inventory
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handlePrintAll}
              className="px-4 py-2 bg-gradient-to-r from-performance-red to-[#93000a] text-white rounded-xl text-xs font-bold uppercase transition-all flex items-center gap-1.5 shadow-lg"
            >
              <span className="material-symbols-outlined text-[16px]">print</span>
              Print All Sheet
            </button>
            <button type="button" onClick={onClose} className="text-tertiary hover:text-white p-1 hover:bg-white/5 rounded-lg">
              <span className="material-symbols-outlined text-[20px]">close</span>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 p-2">
          {items.map((item) => (
            <div key={item.id} className="bg-white/5 border border-white/10 rounded-2xl p-4 text-center flex flex-col items-center justify-between space-y-2">
              <div>
                <p className="text-xs font-bold text-white truncate max-w-[140px]">{item.name}</p>
                <p className="text-[10px] text-tertiary">{item.brand || 'PPF'}</p>
              </div>
              {qrMap[item.id] ? (
                <div className="bg-white p-2 rounded-xl">
                  <img src={qrMap[item.id]} alt={item.name} className="w-28 h-28" />
                </div>
              ) : (
                <div className="w-28 h-28 flex items-center justify-center text-[10px] text-tertiary">Generating...</div>
              )}
              <span className="font-mono text-[10px] text-white font-bold bg-white/10 px-2 py-0.5 rounded">
                {item.qr_code || item.item_code}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function InventoryPage() {
  const queryClient = useQueryClient();
  const { canDelete } = usePermissions();

  const [activeTab, setActiveTab] = useState<'ppf' | 'usages'>('ppf');

  // Search states
  const [search, setSearch] = useState('');

  // QR States
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [qrScanTarget, setQrScanTarget] = useState<'add' | 'deduct'>('add');
  const [showItemQRModal, setShowItemQRModal] = useState(false);
  const [qrTargetItem, setQrTargetItem] = useState<InventoryItem | null>(null);
  const [showPrintAllQRModal, setShowPrintAllQRModal] = useState(false);

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUsageModal, setShowUsageModal] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);

  // Form states
  const [newBrandForm, setNewBrandForm] = useState<{
    name: string;
    category: InventoryItem['category'];
    unit: InventoryItem['unit'];
    current_stock: number | '';
    min_threshold: number | '';
    purchase_price: number | '';
  }>({
    name: '',
    category: 'ppf_roll',
    unit: 'sqft',
    current_stock: '',
    min_threshold: '',
    purchase_price: '',
  });

  const [usageForm, setUsageForm] = useState<{
    qty_used: number | '';
    manual_amount: number | '';
    deduction_mode: 'qty' | 'amount';
    notes: string;
  }>({
    qty_used: '',
    manual_amount: '',
    deduction_mode: 'qty',
    notes: '',
  });

  const [purchaseForm, setPurchaseForm] = useState<{
    qty_added: number | '';
  }>({
    qty_added: '',
  });

  // API Queries
  const { data: inventoryRes, isLoading: isInventoryLoading } = useQuery({
    queryKey: ['inventory'],
    queryFn: () => inventoryAPI.list(),
  });
  const items = (inventoryRes?.data || []) as InventoryItem[];

  // Show all categories in the table
  const ppfRolls = items;

  const { data: usagesRes, isLoading: isUsagesLoading } = useQuery({
    queryKey: ['usagesHistory'],
    queryFn: () => inventoryAPI.usages(),
  });
  const usagesList = usagesRes?.data || [];

  // Filtered PPF list based on search
  const filteredPPF = ppfRolls.filter((i) => {
    const q = search.toLowerCase();
    return !q || i.name.toLowerCase().includes(q) || i.item_code.toLowerCase().includes(q);
  });

  // Calculations
  const totalSqFeet = ppfRolls.filter(i => i.unit === 'sqft').reduce((sum, item) => sum + Number(item.current_stock), 0);
  const lowStockCount = ppfRolls.filter((item) => Number(item.current_stock) <= Number(item.min_threshold)).length;

  const handleQRScanSuccess = (scannedCode: string) => {
    setShowQRScanner(false);
    const cleanCode = scannedCode.trim().toLowerCase();
    const found = ppfRolls.find(
      i => (i.qr_code && i.qr_code.toLowerCase() === cleanCode) ||
           (i.item_code && i.item_code.toLowerCase() === cleanCode) ||
           i.name.toLowerCase() === cleanCode
    );

    if (found) {
      setSelectedItem(found);
      if (qrScanTarget === 'add') {
        setShowPurchaseMutationDialog();
        toast.success(`Found: ${found.name} — Ready to Add Stock`);
      } else {
        setShowUsageMutationDialog();
        toast.success(`Found: ${found.name} — Ready to Deduct Stock`);
      }
    } else {
      toast.error(`No product found matching QR code: "${scannedCode}"`);
    }
  };

  // Mutations
  const createBrandMutation = useMutation({
    mutationFn: (payload: any) => inventoryAPI.create(payload),
    onSuccess: () => {
      toast.success('PPF Brand registered successfully!');
      setShowAddModal(false);
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      setNewBrandForm({
        name: '',
        category: 'ppf_roll',
        unit: 'sqft',
        current_stock: '',
        min_threshold: '',
        purchase_price: '',
      });
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error?.message || 'Failed to add PPF brand');
    },
  });

  const recordUsageMutation = useMutation({
    mutationFn: (payload: any) => inventoryAPI.logUsage(payload),
    onSuccess: () => {
      toast.success('Deduction logged successfully!');
      setShowUsageModal(false);
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['usagesHistory'] });
      setUsageForm({ qty_used: '', manual_amount: '', deduction_mode: 'qty', notes: '' });
      setSelectedItem(null);
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error?.message || 'Failed to register stock deduction');
    },
  });

  const recordPurchaseMutation = useMutation({
    mutationFn: (payload: any) => inventoryAPI.recordPurchase(payload),
    onSuccess: () => {
      toast.success('Stock addition logged successfully!');
      setShowPurchaseModal(false);
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['usagesHistory'] });
      setPurchaseForm({ qty_added: '' });
      setSelectedItem(null);
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error?.message || 'Failed to add stock');
    },
  });

  const handleCommitNewBrand = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newBrandForm.name) {
      toast.error('Item Name is required.');
      return;
    }
    const payload = {
      name: newBrandForm.name,
      category: newBrandForm.category,
      brand: newBrandForm.name,
      unit: newBrandForm.unit,
      current_stock: newBrandForm.current_stock === '' ? 0 : Number(newBrandForm.current_stock),
      min_threshold: newBrandForm.min_threshold === '' ? 10 : Number(newBrandForm.min_threshold),
      purchase_price: newBrandForm.purchase_price === '' ? 0 : Number(newBrandForm.purchase_price),
      selling_price: 0,
      location: 'Main Hangar',
      notes: 'Added via inventory manager',
    };
    createBrandMutation.mutate(payload);
  };

  const handleCommitUsage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedItem) return;
    let qty: number;
    if (usageForm.deduction_mode === 'amount') {
      const price = Number(selectedItem.purchase_price) || 1;
      if (usageForm.manual_amount === '' || Number(usageForm.manual_amount) <= 0) {
        toast.error('Please input a valid amount to deduct.');
        return;
      }
      qty = Number(usageForm.manual_amount) / price;
    } else {
      if (usageForm.qty_used === '' || Number(usageForm.qty_used) <= 0) {
        toast.error('Please input a valid quantity to deduct.');
        return;
      }
      qty = Number(usageForm.qty_used);
    }

    const payload = {
      inventory_item_id: selectedItem.id,
      qty_used: qty,
      manual_amount: usageForm.deduction_mode === 'amount' ? Number(usageForm.manual_amount) : undefined,
      wastage_qty: 0,
      notes: usageForm.notes || (usageForm.deduction_mode === 'amount' ? `Manual amount deduction: ₹${usageForm.manual_amount}` : 'Manual stock deduction'),
    };
    recordUsageMutation.mutate(payload);
  };

  const handleCommitPurchase = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedItem) return;
    if (purchaseForm.qty_added === '' || Number(purchaseForm.qty_added) <= 0) {
      toast.error('Please input a valid quantity to add.');
      return;
    }
    const payload = {
      inventory_item_id: selectedItem.id,
      qty_added: Number(purchaseForm.qty_added),
      purchase_price: Number(selectedItem.purchase_price) || 0,
      supplier: 'Manual Stock In',
      purchase_date: new Date().toISOString().split('T')[0],
      notes: 'Manually added stock to brand rolls',
    };
    recordPurchaseMutation.mutate(payload);
  };

  return (
    <div className="space-y-8 relative z-10">
      {/* HEADER BAR */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 border-b border-white/5 pb-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-[2px] bg-performance-red"></div>
            <span className="font-label-caps text-label-caps text-performance-red tracking-[0.3em] uppercase">
              PPF Inventory Manager
            </span>
          </div>
          <h1 className="font-display-hero text-headline-lg text-white tracking-tight">
            PPF Stock Ledger
          </h1>
          <p className="font-body-lg text-body-lg text-tertiary mt-1.5 max-w-2xl">
            Streamlined tracking of PPF film rolls in square feet with direct deductions and automated low-stock warnings.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => {
              setQrScanTarget('add');
              setShowQRScanner(true);
            }}
            className="px-5 py-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-all font-label-caps text-label-caps tracking-wider flex items-center gap-2 active:scale-95 duration-300 uppercase cursor-pointer select-none"
          >
            <span className="material-symbols-outlined text-[18px]">qr_code_scanner</span>
            <span>SCAN QR TO ADD</span>
          </button>

          <button
            type="button"
            onClick={() => {
              setQrScanTarget('deduct');
              setShowQRScanner(true);
            }}
            className="px-5 py-3 rounded-xl border border-amber-500/30 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 transition-all font-label-caps text-label-caps tracking-wider flex items-center gap-2 active:scale-95 duration-300 uppercase cursor-pointer select-none"
          >
            <span className="material-symbols-outlined text-[18px]">qr_code_scanner</span>
            <span>SCAN QR TO DEDUCT</span>
          </button>

          <button
            type="button"
            onClick={() => setShowPrintAllQRModal(true)}
            className="px-5 py-3 rounded-xl border border-white/10 bg-white/5 text-white hover:bg-white/10 hover:shadow-[0_0_20px_rgba(255,255,255,0.05)] transition-all font-label-caps text-label-caps tracking-wider flex items-center gap-2 active:scale-95 duration-300 uppercase cursor-pointer select-none"
          >
            <span className="material-symbols-outlined text-[18px]">print</span>
            <span>PRINT ALL QR CODES</span>
          </button>

          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            className="px-6 py-3 rounded-xl bg-gradient-to-r from-performance-red to-[#93000a] text-white hover:shadow-[0_0_25px_rgba(255,43,43,0.4)] transition-all font-label-caps text-label-caps tracking-wider flex items-center gap-2 active:scale-95 duration-300 border border-white/10 uppercase"
          >
            <span className="material-symbols-outlined text-[18px]">add_circle</span>
            <span>Add PPF Brand</span>
          </button>
        </div>
      </div>

      {/* OVERVIEW telemetry widgets */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="glass-panel p-6 rounded-2xl relative overflow-hidden flex flex-col justify-between min-h-[110px] shadow-lg">
          <div className="absolute top-0 left-0 w-full h-[2px] bg-performance-red/30"></div>
          <p className="font-label-caps text-[10px] text-tertiary/60 tracking-widest uppercase">
            Total Film Available
          </p>
          <p className="font-display-hero text-3xl font-black text-white tracking-tight mt-2 italic">
            {totalSqFeet.toLocaleString()} <span className="text-xs not-italic font-normal text-tertiary/60">sq feet</span>
          </p>
        </div>

        <div className="glass-panel p-6 rounded-2xl relative overflow-hidden flex flex-col justify-between min-h-[110px] shadow-lg">
          <div className="absolute top-0 left-0 w-full h-[2px] bg-performance-red/30"></div>
          <p className="font-label-caps text-[10px] text-tertiary/60 tracking-widest uppercase">
            Low Stock Brands
          </p>
          <p className="font-display-hero text-3xl font-black text-white tracking-tight mt-2 italic flex items-baseline gap-2">
            <span className={lowStockCount > 0 ? 'text-performance-red animate-pulse' : 'text-white'}>
              {lowStockCount}
            </span>
            <span className="text-xs not-italic font-normal text-tertiary/60">alerts active</span>
          </p>
        </div>

        <div className="glass-panel p-6 rounded-2xl relative overflow-hidden flex flex-col justify-between min-h-[110px] shadow-lg sm:col-span-2 lg:col-span-1">
          <div className="absolute top-0 left-0 w-full h-[2px] bg-performance-red/30"></div>
          <p className="font-label-caps text-[10px] text-tertiary/60 tracking-widest uppercase">
            Tracked Brand Rolls
          </p>
          <p className="font-display-hero text-3xl font-black text-white tracking-tight mt-2 italic">
            {ppfRolls.length} <span className="text-xs not-italic font-normal text-tertiary/60">brands configured</span>
          </p>
        </div>
      </div>

      {/* Navigation tabs */}
      <div className="flex gap-6 border-b border-white/5 shrink-0 select-none mt-2">
        <button
          type="button"
          onClick={() => setActiveTab('ppf')}
          className={`pb-4 font-label-caps text-label-caps tracking-widest transition-all duration-300 relative shrink-0 ${
            activeTab === 'ppf'
              ? 'text-performance-red border-b-2 border-performance-red shadow-[0_4px_12px_rgba(255,43,43,0.15)] font-bold'
              : 'text-tertiary hover:text-white'
          }`}
        >
          PPF BRAND ROLLS
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('usages')}
          className={`pb-4 font-label-caps text-label-caps tracking-widest transition-all duration-300 relative shrink-0 ${
            activeTab === 'usages'
              ? 'text-performance-red border-b-2 border-performance-red shadow-[0_4px_12px_rgba(255,43,43,0.15)] font-bold'
              : 'text-tertiary hover:text-white'
          }`}
        >
          DEDUCTION LOGS
        </button>
      </div>

      {/* TAB 1: PPF BRAND ROLLS */}
      {activeTab === 'ppf' && (
        <div className="bg-[#0c0c0c]/40 backdrop-blur-2xl border border-white/5 rounded-2xl overflow-hidden shadow-2xl flex flex-col">
          {/* Search bar */}
          <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between bg-black/25 flex-wrap gap-4">
            <div className="relative w-full sm:w-auto">
              <span className="material-symbols-outlined absolute left-3.5 top-1/2 -translate-y-1/2 text-tertiary/45 text-[18px]">
                search
              </span>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search PPF Brand or roll code..."
                className="w-full sm:w-64 bg-white/5 border border-white/10 rounded-xl py-2 pl-10 pr-4 text-xs text-white placeholder-tertiary/40 focus:outline-none focus:border-performance-red/50 transition-all font-body-lg"
              />
            </div>
            <div className="text-xs text-tertiary/40 font-data-sm">
              Showing {filteredPPF.length} of {ppfRolls.length} brands
            </div>
          </div>

          {/* Table list */}
          <div className="overflow-x-auto custom-scrollbar">
            <table className="w-full text-left border-collapse min-w-[700px]">
              <thead>
                <tr className="bg-black/35 text-tertiary/70 text-[10px] font-label-caps border-b border-white/5 uppercase tracking-widest">
                  <th className="py-4.5 px-6 font-normal">Roll Code</th>
                  <th className="py-4.5 px-6 font-normal">PPF Brand Name</th>
                  <th className="py-4.5 px-6 font-normal">Available Length</th>
                  <th className="py-4.5 px-6 font-normal">Low Stock Threshold</th>
                  <th className="py-4.5 px-6 font-normal">Date Registered</th>
                  <th className="py-4.5 px-6 font-normal text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5 font-data-sm text-on-surface">
                {isInventoryLoading ? (
                  <tr>
                    <td colSpan={6} className="py-16 text-center text-tertiary/60 font-body-lg italic">
                      Acquiring database telemetry...
                    </td>
                  </tr>
                ) : filteredPPF.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-16 text-center text-tertiary/30 font-body-lg italic">
                      <span className="material-symbols-outlined text-4xl block mb-2 opacity-50">
                        layers
                      </span>
                      NO PPF BRAND ROLLS CONFIGURED
                    </td>
                  </tr>
                ) : (
                  filteredPPF.map((item) => {
                    const isLow = Number(item.current_stock) <= Number(item.min_threshold);
                    return (
                      <tr
                        key={item.id}
                        className={`hover:bg-performance-red/[0.01] border-l-2 transition-all group duration-300 ${
                          isLow ? 'bg-performance-red/[0.005] border-l-performance-red/60' : 'border-l-transparent'
                        }`}
                      >
                        <td className="py-4.5 px-6 text-tertiary/50 font-bold font-data-sm">
                          <div className="flex items-center gap-2">
                            <span>{item.item_code}</span>
                            <button
                              type="button"
                              title="View & Print QR Code"
                              onClick={() => {
                                setQrTargetItem(item);
                                setShowItemQRModal(true);
                              }}
                              className="p-1 rounded bg-white/5 border border-white/10 hover:bg-performance-red hover:border-transparent text-tertiary hover:text-white transition-all active:scale-95 flex items-center justify-center"
                            >
                              <span className="material-symbols-outlined text-[14px]">qr_code_2</span>
                            </button>
                          </div>
                        </td>
                        <td className="py-4.5 px-6">
                          <p className="text-sm font-semibold text-white font-body-lg">
                            {item.name}
                          </p>
                        </td>
                        <td className="py-4.5 px-6">
                          <StockBar qty={Number(item.current_stock)} min={Number(item.min_threshold)} unit={item.unit} />
                          {isLow && (
                            <span className="inline-block px-2 py-0.5 rounded text-[8px] font-extrabold uppercase bg-performance-red/10 border border-performance-red/20 text-performance-red mt-1 animate-pulse">
                              ⚠ LOW STOCK WARNING
                            </span>
                          )}
                        </td>
                        <td className="py-4.5 px-6 font-data-sm text-xs text-white">
                          {item.min_threshold} {formatUnit(item.unit)}
                        </td>
                        <td className="py-4.5 px-6 text-xs text-tertiary/60 font-data-sm">
                          {item.created_at ? new Date(item.created_at).toLocaleDateString() : '—'}
                        </td>
                        <td className="py-4.5 px-6">
                          <div className="flex justify-center gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedItem(item);
                                setShowUsageMutationDialog();
                              }}
                              className="px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 hover:bg-performance-red hover:border-transparent text-white text-[9px] font-label-caps transition-all active:scale-95 font-bold uppercase tracking-wider"
                            >
                              Deduct
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedItem(item);
                                setShowPurchaseMutationDialog();
                              }}
                              className="px-2.5 py-1.5 rounded-lg bg-performance-red/10 border border-performance-red/35 hover:bg-performance-red hover:border-transparent text-white text-[9px] font-label-caps transition-all active:scale-95 font-bold uppercase tracking-wider"
                            >
                              Add Stock
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 2: DEDUCTION LOGS */}
      {activeTab === 'usages' && (
        <div className="bg-[#0c0c0c]/40 backdrop-blur-2xl border border-white/5 rounded-2xl overflow-hidden shadow-2xl">
          <div className="p-5 border-b border-white/5 bg-black/25">
            <h2 className="font-label-caps text-xs text-tertiary tracking-wider uppercase">
              PPF Material Deduction Journal
            </h2>
          </div>

          <div className="overflow-x-auto custom-scrollbar">
            <table className="w-full text-left border-collapse text-xs font-data-sm text-on-surface min-w-[700px]">
              <thead>
                <tr className="bg-black/35 border-b border-white/5 text-tertiary/75 uppercase tracking-widest font-label-caps font-bold">
                  <th className="p-4.5 px-6 font-normal">Deduction Date</th>
                  <th className="p-4.5 px-6 font-normal">PPF Brand</th>
                  <th className="p-4.5 px-6 font-normal">Deducted Qty</th>
                  <th className="p-4.5 px-6 font-normal">Reference Event</th>
                  <th className="p-4.5 px-6 font-normal">Technician Name</th>
                  <th className="p-4.5 px-6 font-normal">Journal Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {isUsagesLoading ? (
                  <tr>
                    <td colSpan={6} className="py-16 text-center text-tertiary/60 font-body-lg italic">
                      Retrieving stock deduction telemetry...
                    </td>
                  </tr>
                ) : usagesList.length > 0 ? (
                  usagesList.map((u: any) => (
                    <tr key={u.id} className="hover:bg-white/[0.01]">
                      <td className="p-4.5 px-6 text-tertiary/80">
                        {new Date(u.created_at).toLocaleString()}
                      </td>
                      <td className="p-4.5 px-6">
                        <span className="text-white font-bold font-body-lg">{u.item_name || 'Generic PPF Roll'}</span>
                      </td>
                      <td className="p-4.5 px-6 text-performance-red font-bold font-data-lg text-sm">
                        -{u.total_deducted || u.qty_used} {formatUnit(u.unit || u.item_unit)}
                      </td>
                      <td className="p-4.5 px-6">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[8px] font-label-caps font-bold tracking-widest uppercase bg-white/5 border border-white/10 text-white">
                          {u.job_code || 'Manual Deduction'}
                        </span>
                      </td>
                      <td className="p-4.5 px-6 text-tertiary/70 font-body-lg">
                        {u.staff_name || 'Hangar Crew'}
                      </td>
                      <td className="p-4.5 px-6 text-tertiary/60 max-w-[200px] truncate font-body-lg">
                        {u.notes || '—'}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="py-16 text-center text-tertiary/30 italic font-body-lg">
                      No stock deductions currently logged.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* MODAL: ADD INVENTORY ITEM */}
      {showAddModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/85 backdrop-blur-md p-4 pt-20 sm:pt-4">
          <div className="bg-[#0c0c0c] border border-white/10 rounded-2xl p-6 max-w-md w-full relative shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="absolute -top-24 -left-24 w-48 h-48 bg-performance-red/[0.04] blur-[60px] pointer-events-none" />
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-display-hero text-lg font-black text-white uppercase tracking-wider">
                Register Inventory Item
              </h3>
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="text-tertiary hover:text-white p-1 hover:bg-white/5 rounded-lg transition-all"
              >
                <span className="material-symbols-outlined text-[20px]">close</span>
              </button>
            </div>

            <form onSubmit={handleCommitNewBrand} className="space-y-4">
              <div>
                <label className="block text-[10px] text-tertiary font-label-caps mb-1.5 uppercase tracking-wider font-bold">
                  Item Name *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. XPEL Ultimate Plus / Meguiar Ceramic Wax"
                  value={newBrandForm.name}
                  onChange={(e) => setNewBrandForm({ ...newBrandForm, name: e.target.value })}
                  className="w-full bg-black border border-white/10 focus:border-performance-red/50 focus:outline-none rounded-xl px-3 py-2.5 text-xs text-white font-body-lg"
                />
              </div>

              <div className="grid grid-cols-2 gap-3 font-data-sm text-xs">
                <div>
                  <label className="block text-[10px] text-tertiary font-label-caps mb-1.5 uppercase tracking-wider font-bold">Category *</label>
                  <select
                    value={newBrandForm.category}
                    onChange={(e) => setNewBrandForm({ ...newBrandForm, category: e.target.value as InventoryItem['category'] })}
                    className="w-full bg-black border border-white/10 focus:border-performance-red/50 focus:outline-none rounded-xl px-3 py-2.5 text-xs text-white outline-none"
                  >
                    <option value="ppf_roll">PPF Roll</option>
                    <option value="ceramic">Ceramic Coating</option>
                    <option value="primer">Primer / Prep</option>
                    <option value="car_care">Car Care Product</option>
                    <option value="consumable">Consumable</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[10px] text-tertiary font-label-caps mb-1.5 uppercase tracking-wider font-bold">Unit of Measurement *</label>
                  <select
                    value={newBrandForm.unit}
                    onChange={(e) => setNewBrandForm({ ...newBrandForm, unit: e.target.value as InventoryItem['unit'] })}
                    className="w-full bg-black border border-white/10 focus:border-performance-red/50 focus:outline-none rounded-xl px-3 py-2.5 text-xs text-white outline-none"
                  >
                    <option value="sqft">Square Feet (sq ft)</option>
                    <option value="ml">Millilitres (ml)</option>
                    <option value="litre">Litres (L)</option>
                    <option value="units">Units / Pieces (pcs)</option>
                    <option value="rolls">Rolls</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 font-data-sm text-xs">
                <div>
                  <label className="block text-[10px] text-tertiary font-label-caps mb-1.5 uppercase tracking-wider font-bold">
                    Initial Stock
                  </label>
                  <input
                    type="number"
                    min="0"
                    required
                    value={newBrandForm.current_stock}
                    onChange={(e) => setNewBrandForm({ ...newBrandForm, current_stock: e.target.value === '' ? '' : Number(e.target.value) })}
                    className="w-full bg-black border border-white/10 focus:border-performance-red/50 focus:outline-none rounded-xl p-2.5 text-white text-right"
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-tertiary font-label-caps mb-1.5 uppercase tracking-wider font-bold">
                    Low Stock Alert
                  </label>
                  <input
                    type="number"
                    min="0"
                    required
                    value={newBrandForm.min_threshold}
                    onChange={(e) => setNewBrandForm({ ...newBrandForm, min_threshold: e.target.value === '' ? '' : Number(e.target.value) })}
                    className="w-full bg-black border border-white/10 focus:border-performance-red/50 focus:outline-none rounded-xl p-2.5 text-white text-right"
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-tertiary font-label-caps mb-1.5 uppercase tracking-wider font-bold">
                    Unit Price (₹)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={newBrandForm.purchase_price}
                    onChange={(e) => setNewBrandForm({ ...newBrandForm, purchase_price: e.target.value === '' ? '' : Number(e.target.value) })}
                    placeholder="0"
                    className="w-full bg-black border border-white/10 focus:border-performance-red/50 focus:outline-none rounded-xl p-2.5 text-white text-right"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 bg-white/5 border border-white/10 py-2.5 rounded-xl text-xs font-label-caps text-tertiary hover:text-white transition-all font-bold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createBrandMutation.isPending}
                  className="flex-1 bg-gradient-to-r from-performance-red to-[#93000a] py-2.5 rounded-xl text-xs font-label-caps text-white hover:shadow-[0_0_20px_rgba(255,43,43,0.3)] transition-all font-bold uppercase tracking-wider"
                >
                  {createBrandMutation.isPending ? 'Registering...' : 'Register Brand'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: STOCK IN ADJUSTMENT */}
      {showPurchaseModal && selectedItem && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/85 backdrop-blur-md p-4 pt-20 sm:pt-4">
          <div className="bg-[#0c0c0c] border border-white/10 rounded-2xl p-6 max-w-sm w-full relative shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="absolute -top-24 -left-24 w-48 h-48 bg-performance-red/[0.04] blur-[60px] pointer-events-none" />
            <div className="flex justify-between items-center mb-6">
              <div className="space-y-1">
                <h3 className="font-display-hero text-lg font-black text-white uppercase tracking-wider">
                  Add Stock
                </h3>
                <p className="text-[10px] text-tertiary/50 font-body-lg">Brand: {selectedItem.name}</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowPurchaseModal(false);
                  setSelectedItem(null);
                }}
                className="text-tertiary hover:text-white p-1 hover:bg-white/5 rounded-lg transition-all"
              >
                <span className="material-symbols-outlined text-[20px]">close</span>
              </button>
            </div>

            <form onSubmit={handleCommitPurchase} className="space-y-4">
              <div>
                <label className="block text-[10px] text-tertiary font-label-caps mb-1.5 uppercase tracking-wider font-bold">
                  Quantity to Add ({formatUnit(selectedItem?.unit)}) *
                </label>
                <input
                  type="number"
                  min="1"
                  required
                  value={purchaseForm.qty_added}
                  onChange={(e) => setPurchaseForm({ qty_added: e.target.value === '' ? '' : Number(e.target.value) })}
                  className="w-full bg-black border border-white/10 focus:border-performance-red/50 focus:outline-none rounded-xl p-3 text-xs text-white text-right font-data-sm"
                />
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowPurchaseModal(false);
                    setSelectedItem(null);
                  }}
                  className="flex-1 bg-white/5 border border-white/10 py-2.5 rounded-xl text-xs font-label-caps text-tertiary hover:text-white transition-all font-bold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={recordPurchaseMutation.isPending}
                  className="flex-1 bg-gradient-to-r from-performance-red to-[#93000a] py-2.5 rounded-xl text-xs font-label-caps text-white hover:shadow-[0_0_20px_rgba(255,43,43,0.3)] transition-all font-bold uppercase tracking-wider"
                >
                  {recordPurchaseMutation.isPending ? 'Adding...' : 'Confirm'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: MANUAL DEDUCTION ADJUSTMENT */}
      {showUsageModal && selectedItem && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/85 backdrop-blur-md p-4 pt-20 sm:pt-4">
          <div className="bg-[#0c0c0c] border border-white/10 rounded-2xl p-6 max-w-sm w-full relative shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="absolute -top-24 -left-24 w-48 h-48 bg-performance-red/[0.04] blur-[60px] pointer-events-none" />
            <div className="flex justify-between items-center mb-6">
              <div className="space-y-1">
                <h3 className="font-display-hero text-lg font-black text-white uppercase tracking-wider">
                  Deduct Stock
                </h3>
                <p className="text-[10px] text-tertiary/50 font-body-lg">Brand: {selectedItem.name}</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowUsageModal(false);
                  setSelectedItem(null);
                }}
                className="text-tertiary hover:text-white p-1 hover:bg-white/5 rounded-lg transition-all"
              >
                <span className="material-symbols-outlined text-[20px]">close</span>
              </button>
            </div>

            <form onSubmit={handleCommitUsage} className="space-y-4">
              {/* Mode toggle */}
              <div className="flex gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => setUsageForm({ ...usageForm, deduction_mode: 'qty' })}
                  className={`flex-1 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${
                    usageForm.deduction_mode === 'qty'
                      ? 'bg-performance-red/20 border border-performance-red/40 text-performance-red'
                      : 'bg-white/5 border border-white/10 text-tertiary'
                  }`}
                >
                  By Quantity
                </button>
                <button
                  type="button"
                  onClick={() => setUsageForm({ ...usageForm, deduction_mode: 'amount' })}
                  className={`flex-1 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${
                    usageForm.deduction_mode === 'amount'
                      ? 'bg-performance-red/20 border border-performance-red/40 text-performance-red'
                      : 'bg-white/5 border border-white/10 text-tertiary'
                  }`}
                >
                  By Amount (₹)
                </button>
              </div>

              {usageForm.deduction_mode === 'qty' ? (
                <div>
                  <label className="block text-[10px] text-tertiary font-label-caps mb-1.5 uppercase tracking-wider font-bold">
                    Quantity Used ({formatUnit(selectedItem?.unit)}) *
                  </label>
                  <input
                    type="number"
                    min="0.01"
                    step="any"
                    required
                    value={usageForm.qty_used}
                    onChange={(e) => setUsageForm({ ...usageForm, qty_used: e.target.value === '' ? '' : Number(e.target.value) })}
                    placeholder={`Amount in ${formatUnit(selectedItem?.unit)}`}
                    className="w-full bg-black border border-white/10 focus:border-performance-red/50 focus:outline-none rounded-xl p-3 text-xs text-white text-right font-data-sm"
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-[10px] text-tertiary font-label-caps mb-1.5 uppercase tracking-wider font-bold">
                    Manual Deduction Amount (₹) *
                  </label>
                  <input
                    type="number"
                    min="1"
                    required
                    value={usageForm.manual_amount}
                    onChange={(e) => setUsageForm({ ...usageForm, manual_amount: e.target.value === '' ? '' : Number(e.target.value) })}
                    placeholder="Enter rupee value used"
                    className="w-full bg-black border border-white/10 focus:border-performance-red/50 focus:outline-none rounded-xl p-3 text-xs text-white text-right font-data-sm"
                  />
                  <p className="text-[10px] text-tertiary/40 mt-1">
                    {selectedItem?.purchase_price && usageForm.manual_amount
                      ? `≈ ${(Number(usageForm.manual_amount) / selectedItem.purchase_price).toFixed(2)} ${formatUnit(selectedItem.unit)} @ ₹${selectedItem.purchase_price}/${formatUnit(selectedItem.unit)}`
                      : 'Quantity calculated using item unit purchase price'}
                  </p>
                </div>
              )}

              <div>
                <label className="block text-[10px] text-tertiary font-label-caps mb-1.5 uppercase tracking-wider font-bold">
                  Deduction Notes / Event *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Job Card GOC-QJ-XXXX"
                  value={usageForm.notes}
                  onChange={(e) => setUsageForm({ ...usageForm, notes: e.target.value })}
                  className="w-full bg-black border border-white/10 focus:border-performance-red/50 focus:outline-none rounded-xl px-3 py-2.5 text-xs text-white font-body-lg"
                />
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowUsageModal(false);
                    setSelectedItem(null);
                  }}
                  className="flex-1 bg-white/5 border border-white/10 py-2.5 rounded-xl text-xs font-label-caps text-tertiary hover:text-white transition-all font-bold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={recordUsageMutation.isPending}
                  className="flex-1 bg-gradient-to-r from-performance-red to-[#93000a] py-2.5 rounded-xl text-xs font-label-caps text-white hover:shadow-[0_0_20px_rgba(255,43,43,0.3)] transition-all font-bold uppercase tracking-wider"
                >
                  {recordUsageMutation.isPending ? 'Deducting...' : 'Confirm'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* QR CAMERA SCANNER MODAL */}
      {showQRScanner && (
        <QRScannerModal
          targetMode={qrScanTarget}
          onScanSuccess={handleQRScanSuccess}
          onClose={() => setShowQRScanner(false)}
        />
      )}

      {/* SINGLE ITEM QR CODE MODAL */}
      {showItemQRModal && qrTargetItem && (
        <ItemQRModal
          item={qrTargetItem}
          onClose={() => {
            setShowItemQRModal(false);
            setQrTargetItem(null);
          }}
        />
      )}

      {/* BULK PRINT ALL QR CODES MODAL */}
      {showPrintAllQRModal && (
        <PrintAllQRModal
          items={ppfRolls}
          onClose={() => setShowPrintAllQRModal(false)}
        />
      )}
    </div>
  );

  function setShowUsageMutationDialog() {
    setUsageForm({ qty_used: '', manual_amount: '', deduction_mode: 'qty', notes: '' });
    setShowUsageModal(true);
  }

  function setShowPurchaseMutationDialog() {
    setPurchaseForm({ qty_added: '' });
    setShowPurchaseModal(true);
  }
}
